/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import cookie from "js-cookie";
import axios from "axios";
import { API_URL } from '../Constants';


/**
 * האם הכתובת של הדף מבקשת רענון קשיח - `?refreshPortal=1`.
 *
 * הכתובת הזו נוצרת במסך הריסיילר ב-CRM ("העתקת כתובת רענון"), והיא קיימת בשביל רגע
 * אחד: מפעיל ששינה עיצוב או הגדרה ורוצה לראות את התוצאה **עכשיו**, בלי להמתין לשני
 * המטמונים שבדרך.
 *
 * כאן היא עושה שני דברים, ושניהם נחוצים - כל אחד לבדו משאיר מטמון אחד במקום:
 * העוגייה בת חמש הדקות נעקפת, והדגל מועבר ל-SSO כדי שהוא ימשוך מהבילינג לפני שיענה.
 */
function wantsFreshSettings() {
  try {
    return new URLSearchParams(window.location.search).get("refreshPortal") === "1";
  } catch {
    return false;
  }
}

export const fetchReseller = createAsyncThunk(
  "reseller/fetchReseller",
  async (_, { rejectWithValue }) => {
    try {
      const refresh = wantsFreshSettings();

      const cached = refresh ? null : cookie.get("resellerData");
      const cachedReseller = cached ? JSON.parse(cached) : null;

      const params = new URLSearchParams(window.location.search);
      const redirectFrom = params.get("redirect_from");

      // `cachedReseller &&` הוא התנאי שהיה חסר: בלעדיו, בקשה בלי `redirect_from`
      // וללא עוגייה הייתה מחזירה null כ"הצלחה", והמסך נשאר בטעינה לנצח - כי
      // RedirectByReseller דורש `reseller.code`. עכשיו היעדר עוגייה פירושו למשוך.
      if (cachedReseller && (!redirectFrom || cachedReseller.redirectUrl === redirectFrom)) {
        return cachedReseller;
      }

      const response = await axios.get(API_URL + "getResellerData", {
        params: {
          ...(redirectFrom ? { redirectUrl: redirectFrom } : {}),
          ...(refresh ? { refreshPortal: "1" } : {}),
        },
      });
      
      if(response?.data?.responseStatus === "OK"){
        const resellerData = response.data.resellerData;

        if (resellerData.redirectUrlAllowed) {
          resellerData.redirectUrl = redirectFrom;

          cookie.set("redirect_from", resellerData.redirectUrl, {
            expires: 1 / 24,
            path: "/",
            secure: true,
            sameSite: "Lax"
          });
        } else {
          resellerData.redirectUrl = null;

          cookie.remove("redirect_from");
        }

        /**
         * המטמון בדפדפן: חמש דקות, ולא שעה.
         *
         * העוגייה הזו נושאת עכשיו את ההגדרות של הריסיילר - ערכת העיצוב, מה מותר
         * בממשק, ואילו קבצים להציג - ולא רק את שמו. שעה של מטמון פירושה שכיבוי
         * ההרשמה בפאנל נאכף בשרת תוך דקות, אך הכפתור נשאר על המסך עד שעה: המבקר
         * לוחץ, ונדחה. חמש דקות הן אותו סדר גודל של רענון התמונה ב-SSO
         * (portalDomains.refreshSeconds), ולכן שני המטמונים מתיישנים יחד.
         *
         * המחיר הוא בקשה אחת ל-getResellerData כל חמש דקות לכל מבקר, וה-SSO עונה
         * עליה מהזיכרון בלי לגעת בבילינג.
         */
        cookie.set("resellerData", JSON.stringify(resellerData), {
          expires: 5 / (24 * 60),
          path: "/",
          secure: true,
          sameSite: "Lax"
        });
  
        return resellerData;
      }else{
        throw new Error("Error");
      }
    } catch (error) {
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const resellerSlice = createSlice({
  name: "reseller",
  initialState: () => {
    return {
      value: null,
      status: "idle",
      error: null,
    }
  },
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(fetchReseller.pending, (state) => {
        state.status = "loading"; 
      })
      .addCase(fetchReseller.fulfilled, (state, action) => {
        state.value = action.payload; // Store the fetched reseller
        state.status = "succeeded";
      })
      .addCase(fetchReseller.rejected, (state, action) => {
        state.error = action.error.message; // Store the error message
        state.status = "failed";
      });
  },
});

export default resellerSlice.reducer;
